import { z } from 'zod';
import { roleSchema } from './common.js';
import { BD_ARMY_RANKS } from '../constants/ranks.js';
import { BATTALION_APPOINTMENTS } from '../constants/appointments.js';

/**
 * Login request body. The identifier is a username (CO / operator) OR a BA
 * Number (officers, whose `username` is set to their normalised BA Number).
 * The field stays named `username` so the existing login path is unchanged;
 * the UI just labels it "Username or BA Number". Normalised server-side.
 */
export const loginRequestSchema = z.object({
  username: z.string().min(1, 'Username or BA Number is required').max(50),
  password: z.string().min(1, 'Password is required').max(128),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

/** Account approval lifecycle. Officers start PENDING_APPROVAL until the CO acts. */
export const userStatusSchema = z.enum(['PENDING_APPROVAL', 'ACTIVE', 'REJECTED']);
export type UserStatus = z.infer<typeof userStatusSchema>;

/**
 * BA Number — the officer's service number, used as their login identity.
 * Canonical display form is uppercased + internally-trimmed; the `username`
 * column stores the lowercased form so the login normaliser matches.
 * Accepts letters, digits, spaces, and hyphens (e.g. "BA-12345", "BA 12345").
 */
export const BA_NUMBER_REGEX = /^[A-Za-z0-9][A-Za-z0-9 -]{2,29}$/;

/** Canonical display form of a BA Number: trim, collapse inner whitespace, upper-case. */
export function normaliseBaNumberDisplay(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ').toUpperCase();
}

/** Login/username form of a BA Number: display form, lower-cased to match the login normaliser. */
export function baNumberToUsername(raw: string): string {
  return normaliseBaNumberDisplay(raw).toLowerCase();
}

/** Maximum length of the canonical login key (BA-number core + a small margin). */
export const LOGIN_KEY_MAX_LENGTH = 10;

/**
 * Tolerant login-key canonicaliser. Returns the shape that two BA-number
 * inputs share regardless of formatting — so "10328", "ba-10328", "BA10328",
 * "ba 10328", "Ba-10328" all collapse to "10328" and can be matched at
 * login regardless of which form the officer typed at registration.
 *
 * Rules: lowercase, strip every non-alphanumeric character, drop a leading
 * "ba" prefix if it's followed by digits. Capped at LOGIN_KEY_MAX_LENGTH so
 * pathologically long input can't sneak past the username column or feed a
 * timing oracle. Returns null when the result is empty (so callers can fall
 * back to the original exact-match path).
 */
export function canonicaliseLoginKey(raw: string): string | null {
  if (typeof raw !== 'string') return null;
  const stripped = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!stripped) return null;
  const withoutPrefix = /^ba\d+$/.test(stripped) ? stripped.slice(2) : stripped;
  if (!withoutPrefix) return null;
  if (withoutPrefix.length > LOGIN_KEY_MAX_LENGTH) return null;
  return withoutPrefix;
}

/**
 * Public-safe user shape returned by /auth/login, /auth/me, and similar.
 * NEVER expose passwordHash, failedLoginAttempts, or lockedUntil to the
 * frontend.
 */
export const publicUserSchema = z.object({
  id: z.string().uuid(),
  username: z.string(),
  baNumber: z.string().nullable(),
  name: z.string(),
  rank: z.string().nullable(),
  role: roleSchema,
  status: userStatusSchema,
  isActive: z.boolean(),
  avatarColor: z.string().nullable(),
  // Battalion appointment (singleton — 2IC | ADJT | QM | null). Renders as a
  // chip next to the name everywhere the user shows up. Phase 7A.
  appointment: z.enum(BATTALION_APPOINTMENTS).nullable(),
  lastLoginAt: z.string().nullable(),
});
export type PublicUser = z.infer<typeof publicUserSchema>;

export interface LoginResponseData {
  user: PublicUser;
  accessToken: string;
  /** Seconds until accessToken expiry. The frontend uses this to schedule a refresh. */
  expiresIn: number;
  /**
   * ONLY present for native clients (`X-Client-Platform: android`), which
   * cannot rely on the HTTP-only cookie and store this in the Android
   * Keystore instead. Never returned to web clients — the cookie carries it.
   */
  refreshToken?: string;
}

export interface RefreshResponseData {
  accessToken: string;
  expiresIn: number;
}

/**
 * Body of POST /auth/refresh and POST /auth/logout. Web clients send no body
 * (the refresh token travels in the HTTP-only cookie); native clients send
 * the Keystore-held token here. 2048 caps well above real JWT size without
 * inviting abuse.
 */
export const refreshRequestSchema = z.object({
  refreshToken: z.string().min(1).max(2048).optional(),
});
export type RefreshRequest = z.infer<typeof refreshRequestSchema>;

/**
 * Self-service profile update for the signed-in user. CO uses this from
 * Settings to set a real display name + rank (e.g. "Lt Col" / "Mohiuddin")
 * so the dashboard greets them by name instead of the generic role label.
 *
 * Both fields are optional so the form can patch one or both. Rank is
 * nullable so the CO can clear it.
 */
export const updateProfileRequestSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'Please enter your name.')
      .max(100, 'Name is too long.')
      .optional(),
    rank: z.string().trim().max(50, 'Rank is too long.').nullable().optional(),
  })
  .refine((v) => v.name !== undefined || v.rank !== undefined, 'Provide a name or rank to update.');
export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>;

/**
 * Officer self-registration. The CO must approve the account before the
 * officer can log in. Rank must be one of the recognised ranks; the only
 * password rule is that it's non-empty (no complexity policy) and that
 * confirmPassword matches. `max(128)` is a sanity cap, not a strength rule.
 */
export const registerOfficerRequestSchema = z
  .object({
    fullName: z
      .string()
      .trim()
      .min(2, 'Please enter your full name.')
      .max(100, 'Name is too long.'),
    rank: z.enum(BD_ARMY_RANKS, { message: 'Please select your rank.' }),
    baNumber: z
      .string()
      .trim()
      .min(3, 'Please enter your BA Number.')
      .max(30, 'BA Number is too long.')
      .regex(BA_NUMBER_REGEX, 'BA Number may use letters, digits, spaces, and hyphens.'),
    password: z.string().min(1, 'Please enter a password.').max(128),
    confirmPassword: z.string().min(1, 'Please confirm your password.'),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'Passwords do not match.',
    path: ['confirmPassword'],
  });
export type RegisterOfficerRequest = z.infer<typeof registerOfficerRequestSchema>;

/** Neutral response after registering — never reveals whether an account already exists. */
export interface RegisterOfficerResponse {
  submitted: true;
  message: string;
}

/** A pending (or recently-actioned) registration shown on the CO approvals screen. */
export const registrationSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  rank: z.string().nullable(),
  baNumber: z.string().nullable(),
  status: userStatusSchema,
  avatarColor: z.string().nullable(),
  appointment: z.enum(BATTALION_APPOINTMENTS).nullable(),
  createdAt: z.string(),
  approvedAt: z.string().nullable(),
});
export type RegistrationSummary = z.infer<typeof registrationSummarySchema>;

/** Reject body — optional short reason recorded in the audit trail. */
export const rejectRegistrationRequestSchema = z.object({
  reason: z.string().trim().max(500, 'Reason is too long.').optional(),
});
export type RejectRegistrationRequest = z.infer<typeof rejectRegistrationRequestSchema>;

/** A person in the directory (CO + active officers) — entry point for DMs and board membership. */
export const directoryUserSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  rank: z.string().nullable(),
  baNumber: z.string().nullable(),
  role: roleSchema,
  avatarColor: z.string().nullable(),
  appointment: z.enum(BATTALION_APPOINTMENTS).nullable(),
});
export type DirectoryUser = z.infer<typeof directoryUserSchema>;
